import { DatePipe } from "@angular/common";
import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
} from "@angular/core";
import { BsModalService } from "ngx-bootstrap/modal";
import { debounceTime, firstValueFrom, Subject, Subscription, tap } from "rxjs";
import { AppState, DbEntityChangedEvent, RoleMgr, ShowRequest } from "../../core";
import { Volunteer } from "../models/volunteer";
import { UserService } from "../../core/services";
import { VolunteerService } from "../services/volunteer.service";
import {
  TableRecActionOkResult,
  TableRecActionResult,
  TableRecsSequentialAction,
} from "../../shared/components/table-view/table-view-action";
import {
  TableColumn,
  TableHeaderTemplateContext,
  TableViewComponentBase,
  TableViewContext,
  TableViewQueryRequest,
} from "../../shared/components/table-view/table-view-component-base";
import { VolunteerEditComponent } from "../volunteer-edit/volunteer-edit.component";
import { VolunteerTaskEditComponent } from "../volunteer-task-edit/volunteer-task-edit.component";
import {
  VolunteerTaskRecord,
  VolunteerTaskViewRecord,
  VolunteerTaskViewTaskPoint,
} from "../models/volunteer-task-record";
import { VolunteerTaskViewService } from "../services/volunteer-task-view.service";
import { VolunteerTaskService } from "../services/volunteer-task.service";
import { activity_status } from "../../core/models";
import Names = activity_status.Names;
import { ConfirmModalComponent } from "../../shared/components/confirm-modal/confirm-modal.component";

@Component({
  selector: "app-volunteer-tasks-table",
  templateUrl: "../../shared/components/table-view/table-view.component.html",
  styleUrls: [
    "../../shared/components/table-view/scss/table-view.scss",
    "../../shared/components/table-view/scss/app-tab-full-height.scss",
    "../../shared/components/table-view/scss/fixed-headers-and-column.scss",
  ],
})
export class VolunteerTasksTableComponent
  extends TableViewComponentBase<VolunteerTaskViewRecord>
  implements OnInit, OnDestroy
{
  @Input("editColumnHeaderTmplRef") set editColumnHeaderTmplRefSetter(
    ref: TemplateRef<TableHeaderTemplateContext>
  ) {
    console.log("set _editColumnHeaderTmplRef");
    const column = this.tableColumns.find((c) => c.template == "edit-task");
    if (column) {
      column.headerTemplateRef = ref;
    }
  }

  @Input("chooseVolunteerTmplRef") chooseVolunteerTmplRef: TemplateRef<any>;

  filterKeyUpSubscription: Subscription;

  private statusColumn: TableColumn;
  private volunteerNameColumn: TableColumn;

  constructor(
    appState: AppState,
    modalService: BsModalService,
    public volunteerService: VolunteerService,
    public volunteerTaskService: VolunteerTaskService,
    public volunteerTaskViewService: VolunteerTaskViewService,
    elementRef: ElementRef,
    datePipe: DatePipe,
    public roleMgr: RoleMgr,
    public userService: UserService
  ) {
    super(appState, modalService, elementRef, datePipe);

    const idColumn = {
      title: "ID",
      name: "id",
      template: "number",
      visible: false,
    };
    this.statusColumn = {
      title: "Status",
      name: "statusName",
      visible: true,
      headerCellCssClass: "status-header",
      //filter: { key: "statusName", value:"archived", mode:"!="}
    };
    this.volunteerNameColumn = {
      title: "Assigned To",
      name: "volunteerName",
      template: "volunteerLink",
      visible: true
    };
    this.tableColumns = [
      idColumn,
      {
        title: "Date",
        name: "scheduledDate",
        template: "date",
        visible: true,
      },
      {
        title: "End Time (local)",
        name: "endDate",
        template: "localtime",
        visible: true,
      },
      { title: "Stage", name: "stage", visible: true, filter: {key:'stage', mode:'=', value: ''} },
      { title: "Type", name: "type", visible: false },

      { title: "Task Series ID", name: "seriesId", visible: false, filter: {key:'seriesId', mode:'=', value: ''} },
      { title: "Task#", name: "seriesPosition", visible: true, filter: {key:'seriesPosition', mode:'=', value: ''} },
      { title: "Duration (hrs)", name: "scheduledDurationHrs", template: 'hours', visible: false },

      { title: "Default Duration (hrs)", name: "seriesTaskDurationHrs", template: 'hours', visible: false },
      { title: "Default Interval (hrs)", name: "seriesTaskIntervalHrs", template: 'hours', visible: false },
      { title: "Series Max Count", name: "seriesMaxCnt", visible: false },

      { title: "Building ID", name: "buildingId", visible: false },
      { title: "Building", name: "buildingName", visible: true },
      { title: "Point(s)", name: "taskPoints", visible: true, sortable: false },
      { title: "Test Summary", name: "testSummary", visible: false, sortable: false },
      { title: "Notes", name: "notes", visible: false },
      { title: "Last Test Point ID", name: "lastActiveTaskPointId", visible: false },
      { title: "Last RUT ID", name: "lastValidRutId", visible: false },
      { title: "Last Call", name: "lastCallStartTime", template: 'localtime', visible: true, smallNullSort: true },
      { title: "Last Call Duration", name: "lastCallDuration", visible: false },
      {
        title: "Modified (local)",
        name: "modifiedAt",
        visible: false,
      },
      { title: "Assigned ID", name: "volunteerId", template: "volunteerLink", visible: false },
      this.volunteerNameColumn,
      { title: "Expired", name: "expired", visible: false },
      this.statusColumn,
      { title: "Edit", name: "", visible: true, template: "edit-task" },
    ];

    this.virtualColNames = {
      // need virtual col mapping for any field name used more than once in tableColumns array
      testSummary: "taskPoints"
    };

    this.maxSortColumns = 4;
    this.sortColumns = [
      { key: 'scheduledDate', order: 'DESC'},
      { key: 'seriesPosition', order: 'ASC'},
      { key: 'buildingId', order: 'ASC'},
      { key: 'volunteerName', order: 'ASC'}
    ];

    const rowActions = [
      new TableRecsSequentialAction<VolunteerTaskViewRecord>(
        (this.roleMgr.canEditTasks ? "Edit" : "View") + " Volunteer",
        ({rec})=> this.onShowAssignedUserAction(rec)
      ).setBulkable(false),
    ];

    if (this.roleMgr.canEditTasks) {
      rowActions.push(
        new TableRecsSequentialAction<VolunteerTaskViewRecord>("Delete", ({rec})=>this.onDeleteTaskAction(rec))
          .setBulkable(false)
          .setCssClass("bg-danger")
          .setOnComplete(({recs, result}) => {
            if (result.ok) {
              this.load().catch(this.publishError);
            }
          })
      );
    }

    this.setRowActions(rowActions);

    this.idColumns = [idColumn];
    //this.bStickyBulkActions = true;
    this.bShowBulkActionsColumn = false;
    this.pageSize = 40; // no paging if null
    this.bShowHeader = true;
    this.reloadSub?.unsubscribe();
    this.reloadSub = this.reloadSubject.pipe(
      debounceTime(5_000) // 5 secs before reloading (revisit if lots of volunteers)
    ).subscribe({
      next: () => {
        this.load().catch(e=>this.volunteerTaskService.publishError(e,this,5))
      },
      error: (e) => this.volunteerTaskService.publishError(e, this)
    });

  }

  initKeyUpSubscription = () => {
    this.filterKeyUpSubscription?.unsubscribe();
    this.filterKeyUpSubscription = this.filterKeyUp$
      .pipe(
        tap((e) => e.event.key == "Enter" && this.initKeyUpSubscription()), // reset if enter force load already
        debounceTime(700)
      )
      .subscribe(() => { this.load().catch(this.publishError)} );
  };

  ngOnInit(): void {
    this.initKeyUpSubscription();
    this.init();
    this.initDbEntityChangeSubscription();
  }

  ngOnDestroy(): void {
    this.filterKeyUpSubscription?.unsubscribe();
    this.reloadSub?.unsubscribe();
    this.dbEntityChangeSub?.unsubscribe();
    this.destroy();
  }

  dbEntityChangeSub?: Subscription;
  reloadSub?: Subscription;
  private reloadSubject = new Subject<void>();

  initDbEntityChangeSubscription = () => {
    this.dbEntityChangeSub?.unsubscribe();
    this.dbEntityChangeSub = this.appState.dbEntityChange$.subscribe(this.onEntityChanged);
  }

  onEntityChanged = (changeEvent: DbEntityChangedEvent) => {
    console.log(`VolunteerTasksTableComponent.onEntityChanged: ${JSON.stringify(changeEvent)}`);
    if ( ['volunteer_task_change','volunteer_task_series_change'].includes(changeEvent.channel) ) {
        this.reloadSubject.next();
    }
  }


  showBuilding = (record: VolunteerTaskViewRecord) => {
    this.showTabFn(new ShowRequest("tab", "building", record.buildingId, null));
  };

  showCallLog = (record: VolunteerTaskViewRecord) => {
    const queryForTaskPoint: Partial<TableViewContext> = {
      filters: [{
        key: 'taskId',
        value: `${record.id}`,
        mode: '='
      }],
    };
    this.appState.showView.next(
      new ShowRequest('tab', 'rut call logs', JSON.stringify(queryForTaskPoint))
    );
  };

  showVolunteerPoint = (
    task: VolunteerTaskViewRecord,
    taskPoint: VolunteerTaskViewTaskPoint
  ) => {
    //this.showTabFn(new ShowRequest("tab", "point", vPt.pointId, null));
    this.showTabFn(
      new ShowRequest(
        "tab",
        "volunteer task",
        JSON.stringify({ taskId: task.id, taskPointId: taskPoint.id })
      )
    );
  };

  showVolunteerTask(task: VolunteerTaskViewRecord) {
    //console.log(`onShowVolunteerTask(${task?.id} (bldg:${task?.buildingId}))`);
    this.showTabFn(new ShowRequest("tab", "volunteer task", task.id));
  }

  doQuery = async (queryFilter: TableViewQueryRequest) => {
    queryFilter = JSON.parse(JSON.stringify(queryFilter));
    // Exclude 'archived' tasks explicitly (for now)
    queryFilter.filters.push({ key: "statusName", value:"archived", mode:"!="});
    return this.volunteerTaskViewService.find(queryFilter);
  };

  callCntColumns: TableColumn[] = [
    { title: "Missing Calls", name: "totalMissingCalls", visible: true },
    { title: "Complete Calls", name: "totalCalls", visible: true },
    { title: "Assigned Calls", name: "targetTotalCalls", visible: true },
    { title: "Complete Calls %", name: "callsCompletePct", visible: true },
  ];

  override updateColumnsForView(viewName:string){
    const cntColIndex = this.tableColumns.findIndex(c=>c===this.callCntColumns[0]);
    if ( ['callCounts','addTestSummaryColumn','inProgressStatus','buildingCallCounts','userCallCounts'].includes(viewName) ) {
      if ( cntColIndex === -1 ) {
        const lastCallTimeColIndex = this.tableColumns.findIndex(c=>c.name==='lastCallStartTime');
        this.tableColumns.splice(lastCallTimeColIndex+1,0,...this.callCntColumns);
        this.callCntColumns.forEach(c=>c.visible=true);
      }
    } else if ( cntColIndex !== -1 ) {
      this.tableColumns.splice(cntColIndex,this.callCntColumns.length);
      const colNames = this.callCntColumns.map(c=>c.name);
      this.sortColumns = this.sortColumns.filter(tvs=>!colNames.includes(tvs.key));
    }
  }

  async onEditVolunteerTask(task: VolunteerTaskViewRecord) {
    //console.log(`onEditVolunteerTask(${task?.id} (bldg:${task?.buildingId}))`);
    const initialState = {};
    try {
      const modalRef = this.modalService.show(VolunteerTaskEditComponent, {
        ignoreBackdropClick: true,
        class: "modal-xl",
        initialState,
      });
      const taskEditComponent: VolunteerTaskEditComponent = modalRef.content;
      if (!task) {
        task = new VolunteerTaskViewRecord();
      }
      taskEditComponent.setTask(task);
      const doAfterSave = async () => {
        if (!task?.id) {
          // new row so reload table (table remembers filters/sort so new row may not be visible!)
          await this.load();
        } else {
          const modifiedTask = taskEditComponent.task;
          this.updateRows([modifiedTask]);
        }
      };
      taskEditComponent.doAfterSaveFn = () => {
        doAfterSave().catch(this.publishError) ;
      };
      const doAfterDelete = async (deletedRec) => {
        this.appState.notifyInfo(
          `Volunteer task deleted for building '${deletedRec.buildingId}'.`,
          this,
          3
        );
        await this.load();
      };
      taskEditComponent.doAfterDeleteFn = (rec) => {
        doAfterDelete(rec).catch(this.publishError);
      }

      await firstValueFrom(modalRef.onHidden);
    } catch (error) {
      console.error(error);
      this.publishError(error);
    }
  }

  publishError = (e:any,expireSecs=60) => {
    this.volunteerTaskService.publishError(e,this,expireSecs);
  }

  savingStatusTaskId: number = null;
  assignableStatuses: Array<Volunteer> = [];

  onShowAssignedUserAction = async (task: VolunteerTaskViewRecord) => {
    try {
      const volunteer = await this.volunteerService.getByIdEagerly(task.volunteerId);
      if (volunteer) {
        const modal = VolunteerEditComponent.showModal(this.modalService,volunteer);
        modal.onHidden.subscribe((data) => {
          this.load().catch(this.publishError);
        });
      } else {
        this.appState.notifyWarning(
          `Could not find volunteer (id=${task.volunteerId}, name=${task.volunteerName})`,
          this,
          5
        );
      }
      return new TableRecActionOkResult(task);
    } catch (error) {
      console.error(error);
      return new TableRecActionResult(
        task,
        false,
        this.volunteerService.extractErrorMsg(error)
      );
    }
  };

  onDeleteTaskAction = async (task: VolunteerTaskViewRecord) => {
    let deletedTask: VolunteerTaskRecord = null;
    try {
      deletedTask = await this.volunteerTaskViewService.promptedDeleteTask(task);
      const bDeleted = !!deletedTask;
      return new TableRecActionResult(
        task,
        bDeleted,
        bDeleted ? "Task deleted" : ""
      );
    } catch (error) {
      console.error(error);
      return new TableRecActionResult(
        task,
        false,
        error?.message || "Failed deleting task"
      );
    }
  };


  onTaskStatusChange = async (
    task: VolunteerTaskViewRecord,
    eventTarget: EventTarget
  ) => {
    try {
      this.savingStatusTaskId = task.id;
      const htmlSelectElement = eventTarget as HTMLSelectElement;
      console.log(JSON.stringify(htmlSelectElement.value));
      let selectedStatus = parseInt(htmlSelectElement.value);
      htmlSelectElement.value = `${task.status}`; // set drop down to old value until db save is successful

      let doSave = true;
      if ( selectedStatus === activity_status.Id.ARCHIVED ) {
        doSave = false;
        const initialState = {
          onYes: () => doSave = true,
          title: `Archive Task?`,
          question: `<div class="pt-2">Archive task and remove from task list?</div>
              <div class="m-2 small font-italic text-danger">
              <i class='text-warning fa fa-exclamation-triangle mr-1'></i>
              Cannot be reversed from this application.
              If the task is the latest in the series, the series will also be cancelled.</div>`,
          focusOnNo: true,
          yesStyle: 'btn btn-danger'
        };
        const modalRef = this.modalService.show(ConfirmModalComponent, {
            ignoreBackdropClick: true,
            id: 20230706,
            initialState
        });
        await firstValueFrom(modalRef.onHidden);
      }

      if ( doSave ) {
        const updatedTask = await this.saveTaskStatus(task, selectedStatus);
        Object.assign(task,updatedTask);
        this.appState.notifySuccess(`Status '${Names[selectedStatus]}' saved`, this, 3);
        if ( this.statusColumn.filter ) {
          this.load().catch(this.publishError);
        }
      }
    } catch (error) {
      console.error(error);
      const errorMsg = this.volunteerTaskViewService.extractErrorMsg(error);
      this.appState.notifyError('Issue saving task status. '+errorMsg,this, 5);
      this.publishError(error);
    } finally {
      this.savingStatusTaskId = null;
    }
  };

  private saveTaskStatus = async (
    task: VolunteerTaskViewRecord,
    status: number
  ) => {
    const patchedTask = await this.volunteerTaskViewService.patch({
      id: task.id,
      status,
    });
    this.updateRows([patchedTask]);
    return patchedTask;
  };


  onTabSelected = (bSelected: boolean) => {
    console.log("volunteer-task-list tab selected: " + bSelected);
    if (bSelected) {
      if (!this.bInitialized) {
        this.initialize();
      } else {
        this.load().catch(this.publishError);
      }
    }
  };

  getName = () => "volunteer-tasks-table";
}
